import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Search, UserPlus, Target } from "lucide-react";
import { OPP_PRODUCTS, PRIORITIES, LEAD_ORIGINS } from "@/lib/crm";
import { CUSTOMER_TYPES } from "@/lib/assignments";

const EMPTY_CUST = { type: "Privato", first_name: "", last_name: "", company_name: "", phone_primary: "", lead_origin: "", privacy_notice_provided: true };
const EMPTY_LEAD = { product: "Fotovoltaico", priority: "Media", next_action: "", next_action_date: "", notes: "" };

// Creazione lead: seleziona/crea cliente + crea l'opportunità iniziale (operatori: propria sede; Direzione/Back Office: scelgono la sede).
export default function NewLeadDialog({ open, onOpenChange, onSaved, cross = false }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // {id, display_name}
  const [cust, setCust] = useState(EMPTY_CUST);
  const [lead, setLead] = useState(EMPTY_LEAD);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("existing"); setTerm(""); setResults([]); setSelected(null);
    setCust({ ...EMPTY_CUST }); setLead({ ...EMPTY_LEAD }); setError(""); setOfficeId("");
    if (cross) api.get("/offices").then(({ data }) => setOffices(data.filter((o) => o.active !== false))).catch(() => {});
  }, [open, cross]);

  useEffect(() => {
    if (mode !== "existing" || !term.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/customers", { params: { search: term.trim(), page_size: 8 } });
        setResults(data.items || []);
      } catch (e) { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [term, mode]);

  const setC = (k, v) => setCust((s) => ({ ...s, [k]: v }));
  const setL = (k, v) => setLead((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setError(""); setSaving(true);
    try {
      let customerId = selected?.id;
      if (mode === "new") {
        if (cross && !officeId) { setError("Indica la sede del lead."); setSaving(false); return; }
        const payload = {
          type: cust.type,
          first_name: cust.type === "Privato" ? cust.first_name : null,
          last_name: cust.type === "Privato" ? cust.last_name : null,
          company_name: cust.type === "Azienda" ? cust.company_name : null,
          phone_primary: cust.phone_primary,
          lead_origin: cust.lead_origin || "Altro",
          privacy_notice_provided: !!cust.privacy_notice_provided,
          ...(cross && officeId ? { office_id: officeId } : {}),
        };
        const { data: c } = await api.post("/customers", payload);
        if (c.duplicate && c.customer) { setError(`Cliente già presente: ${c.customer.display_name}. Usa «Cliente esistente».`); setSaving(false); return; }
        customerId = c.id;
      }
      if (!customerId) { setError("Seleziona o crea un cliente."); setSaving(false); return; }
      const { data: opp } = await api.post("/opportunities", {
        customer_id: customerId,
        product: lead.product,
        stage: "Nuovo lead",
        priority: lead.priority,
        next_action: lead.next_action,
        next_action_date: lead.next_action_date || null,
        lead_origin: lead.lead_origin || null,
        notes: lead.notes || null,
      });
      if (!opp.assignment_id) {
        toast.warning("Opportunità creata, ma nessuna pratica risulta in Coda Lavori: segnalalo alla Direzione.");
      } else if (opp.assignment_reused) {
        toast.info("Lead salvato e agganciato alla pratica già aperta di questo cliente in Coda Lavori (nessun doppione).", { duration: 7000 });
      } else {
        toast.success(`Lead salvato in Coda Lavori (${opp.assignment_status})`);
      }
      onSaved?.(opp);
      onOpenChange(false);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Target className="h-5 w-5 text-brand" /> Nuovo Lead</DialogTitle>
          <DialogDescription>{cross ? "Crea un lead per un cliente di qualsiasi sede. Finirà subito in Coda Lavori, intestato a te." : "Crea un lead per un cliente della tua sede. Finirà in Coda Lavori e avviserà il Back Office."}</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="new-lead-error">{error}</div>}

        <div className="space-y-4">
          {/* Cliente */}
          <div className="flex gap-2">
            <Button type="button" variant={mode === "existing" ? "default" : "outline"} size="sm" className="gap-1.5" onClick={() => setMode("existing")} data-testid="lead-mode-existing"><Search className="h-4 w-4" /> Cliente esistente</Button>
            <Button type="button" variant={mode === "new" ? "default" : "outline"} size="sm" className="gap-1.5" onClick={() => setMode("new")} data-testid="lead-mode-new"><UserPlus className="h-4 w-4" /> Nuovo cliente</Button>
          </div>

          {mode === "existing" ? (
            <div className="space-y-2">
              <Input value={term} onChange={(e) => { setTerm(e.target.value); setSelected(null); }} placeholder="Cerca per nome, cognome o azienda…" data-testid="lead-customer-search" />
              {searching && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Ricerca…</div>}
              {!!results.length && (
                <div className="max-h-44 overflow-auto rounded-lg border border-border">
                  {results.map((c) => (
                    <button key={c.id} type="button" onClick={() => { setSelected(c); setTerm(c.display_name); setResults([]); }} data-testid={`lead-customer-${c.id}`}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-brand/5">
                      <span>{c.display_name}</span>
                      <span className="text-xs text-slate-400">{c.phone_primary || ""}</span>
                    </button>
                  ))}
                </div>
              )}
              {selected && <div className="rounded-lg border border-brand/25 bg-brand/5 px-3 py-2 text-sm" data-testid="lead-customer-selected">Cliente selezionato: <strong>{selected.display_name}</strong>{selected.office_name ? <span className="text-slate-500"> · sede {selected.office_name}</span> : ""}</div>}
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-border p-3">
              {cross && (
                <div className="space-y-1.5"><Label>Sede del lead *</Label>
                  <Select value={officeId} onValueChange={setOfficeId}>
                    <SelectTrigger data-testid="lead-office"><SelectValue placeholder="Scegli sede…" /></SelectTrigger>
                    <SelectContent className="bg-white">{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tipo</Label>
                  <Select value={cust.type} onValueChange={(v) => setC("type", v)}>
                    <SelectTrigger data-testid="lead-cust-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{CUSTOMER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Telefono</Label><Input value={cust.phone_primary} onChange={(e) => setC("phone_primary", e.target.value)} data-testid="lead-cust-phone" /></div>
              </div>
              {cust.type === "Privato" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Nome</Label><Input value={cust.first_name} onChange={(e) => setC("first_name", e.target.value)} data-testid="lead-cust-first" /></div>
                  <div className="space-y-1.5"><Label>Cognome</Label><Input value={cust.last_name} onChange={(e) => setC("last_name", e.target.value)} data-testid="lead-cust-last" /></div>
                </div>
              ) : (
                <div className="space-y-1.5"><Label>Ragione sociale</Label><Input value={cust.company_name} onChange={(e) => setC("company_name", e.target.value)} data-testid="lead-cust-company" /></div>
              )}
              <div className="space-y-1.5"><Label>Origine</Label>
                <Select value={cust.lead_origin || "none"} onValueChange={(v) => setC("lead_origin", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="lead-cust-origin"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{LEAD_ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Lead */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Prodotto</Label>
              <Select value={lead.product} onValueChange={(v) => setL("product", v)}>
                <SelectTrigger data-testid="lead-product"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{OPP_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Priorità</Label>
              <Select value={lead.priority} onValueChange={(v) => setL("priority", v)}>
                <SelectTrigger data-testid="lead-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-brand/25 bg-brand/5 p-3">
            <div className="space-y-1.5"><Label className="text-brand">Prossima azione *</Label><Input value={lead.next_action} onChange={(e) => setL("next_action", e.target.value)} placeholder="Es. Chiamare per sopralluogo" data-testid="lead-next-action" /></div>
            <div className="space-y-1.5"><Label className="text-brand">Data *</Label><Input type="date" value={lead.next_action_date} onChange={(e) => setL("next_action_date", e.target.value)} data-testid="lead-next-date" /></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={lead.notes} onChange={(e) => setL("notes", e.target.value)} rows={2} data-testid="lead-notes" /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-cancel">Annulla</Button>
          <Button onClick={submit} disabled={saving} className="gap-1.5" data-testid="lead-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
